'use client';

import { useEffect, useMemo, useState } from 'react';

type Selection = { id: string; handle: string };

type PermissionTree = {
  handle: string;
  id: string;
  status: number;
  children: PermissionTree[];
};

const makeSelection = (node: PermissionTree): Selection => ({
  id: node.id,
  handle: node.handle,
});

const useFilePermissions = (
  tree: PermissionTree[],
  groupHandle: string,
  fileTypeHandles: string[]
) => {
  const [selectedGroup, setSelectedGroup] = useState({ id: '', handle: '' });
  const [selectedType, setSelectedType] = useState({ id: '', handle: '' });
  const [selectedScheme, setSelectedScheme] = useState({ id: '', handle: '' });
  const [selectedOrientation, setSelectedOrientation] = useState({
    id: '',
    handle: '',
  });

  useEffect(() => {
    if (tree.length > 0 && !selectedGroup.handle) {
      const root = tree.find(({ handle }) => handle === groupHandle);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (root) setSelectedGroup(makeSelection(root));
    }
  }, [tree, selectedGroup, groupHandle, setSelectedGroup]);

  const displayTypes = useMemo(() => {
    const group = tree.find((leaf) => leaf.handle === selectedGroup.handle);
    if (!group) return [];

    return group.children.map(makeSelection).filter(({ handle }) => {
      if (fileTypeHandles.length === 0) return true;

      const i = fileTypeHandles.findIndex(
        (typeHandle) => typeHandle === handle
      );

      return i > -1;
    });
  }, [selectedGroup, fileTypeHandles, tree]);

  useEffect(() => {
    if (displayTypes.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (!selectedType.handle) setSelectedType(displayTypes[0]);
      else {
        const valid = displayTypes.find(({ id }) => id === selectedType.id);
        if (!valid) setSelectedType(displayTypes[0]);
      }
    }
  }, [displayTypes, selectedType]);

  const displayOrientations = useMemo(() => {
    const group = tree.find((leaf) => leaf.handle === selectedGroup.handle);
    if (!group) return [];

    const type = group.children.find(
      (leaf) => leaf.handle === selectedType.handle
    );

    if (!type) return [];

    return type.children.map(makeSelection);
  }, [selectedGroup, selectedType, tree]);

  useEffect(() => {
    if (displayOrientations.length > 0) {
      if (!selectedOrientation.handle)
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedOrientation(displayOrientations[0]);
      else {
        const valid = displayOrientations.find(
          ({ id }) => id === selectedOrientation.id
        );
        if (!valid) setSelectedOrientation(displayOrientations[0]);
      }
    }
  }, [displayOrientations, selectedOrientation]);

  const displaySchemes = useMemo(() => {
    const group = tree.find((leaf) => leaf.handle === selectedGroup.handle);
    if (!group) return [];

    const type = group.children.find(
      (leaf) => leaf.handle === selectedType.handle
    );

    if (!type) return [];

    const orientation = type.children.find(
      (leaf) => leaf.handle === selectedOrientation.handle
    );

    if (!orientation) return [];

    return orientation.children.map(makeSelection);
  }, [selectedGroup, selectedType, selectedOrientation, tree]);

  useEffect(() => {
    if (displaySchemes.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (!selectedScheme.handle) setSelectedScheme(displaySchemes[0]);
      else {
        const valid = displaySchemes.find(({ id }) => id === selectedScheme.id);
        if (!valid) setSelectedScheme(displaySchemes[0]);
      }
    }
  }, [displaySchemes, selectedScheme]);

  return {
    displayTypes,
    displayOrientations,
    displaySchemes,
    selectedGroup,
    setSelectedGroup,
    selectedType,
    setSelectedType,
    selectedOrientation,
    setSelectedOrientation,
    selectedScheme,
    setSelectedScheme,
  };
};

export default useFilePermissions;
